import React from 'react';
import { compose, withProps, withHandlers, lifecycle } from 'recompose';
import { reset } from 'redux-form';
import { withTranslation } from 'react-i18next';
import { notifyError, notifySuccess } from '../../../utils/notify';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import createDialog from '../../../store/ui/dialogs/create';
import ActivitySelect from '../../../common/selectors/Activity';
import ProjectMilestoneSelect from '../../../common/selectors/ProjectMilestone';
import RecipientSelect from '../../../common/selectors/Recipient';
import ConfirmRemoveObject from '../../../components/dialogs/ConfirmRemoveObject';
import { apiArchiveActivity } from '../../../common/api/zedoc';
import { createSelector } from 'reselect';
import Stack from '../../../common/components/primitives/Stack';
import Text from '../../../common/components/base/Text';

export const dialog = createDialog(
  'screens_projects_dashboard_archiveActivity',
);

export default compose(
  dialog.connect({
    selectors: {
      // Preserve activityId and milestone_name from dialog props as fallbacks
      activityId: dialog.getProp('activityId'),
      milestone_name: dialog.getProp('milestone_name'),
      recipient_name: dialog.getProp('recipient_name'),

      activity: ActivitySelect.one().whereIdEquals(
        dialog.getProp('activityId'),
      ),

      milestone: ProjectMilestoneSelect.one().whereIdEquals(
        createSelector(
          ActivitySelect.one().whereIdEquals(dialog.getProp('activityId')),
          (activity) => activity?.milestoneId,
        ),
      ),

      recipient: RecipientSelect.one().whereIdEquals(
        createSelector(
          ActivitySelect.one().whereIdEquals(dialog.getProp('activityId')),
          (activity) => activity?.recipientId,
        ),
      ),
    },
  }),
  withTranslation(),

  lifecycle({
    componentDidMount() {
      const { dispatch, open } = this.props;
      if (open) {
        dispatch(reset(dialog.name));
      }
    },
    componentDidUpdate(prevProps) {
      const { dispatch, open, activityId } = this.props;
      // Reset form when dialog opens or when activityId changes
      if (open && (!prevProps.open || activityId !== prevProps.activityId)) {
        dispatch(reset(dialog.name));
      }
    },
  }),

  withProps(
    ({
      activity,
      milestone,
      recipient,
      t,
      activityId,
      milestone_name,
      recipient_name,
    }) => {
      // If selectors temporarily fail (e.g., after tab switch), use fallback values from dialog props
      // This prevents the form from losing its state when DDP connection is reconnecting
      if (!activity || !milestone || !recipient) {
        // Use stored values from dialog props as fallbacks
        const fallbackMilestoneName = milestone_name || '';
        const fallbackRecipientName = recipient_name || '';

        return {
          id: activityId, // Preserve activityId
          form: dialog.name,
          title: (
            <>
              <Text.Span importance="high">
                {t('permissions:patientMilestone.archiveActivity')}
              </Text.Span>
              {' — '}
              <Text.Span>{fallbackMilestoneName}</Text.Span>
              {' — '}
              <Text.Span>{fallbackRecipientName}</Text.Span>
            </>
          ),
          confirmText: t('remove'),
          expectedValue: fallbackMilestoneName, // Preserve expectedValue to keep form validation
          expectedValueLabel: t('confirmations:archiveActivity.label'),
          message: (
            <Stack>
              <Text.Paragraph>
                {t('confirmations:archiveActivity.title')}
              </Text.Paragraph>
              <Text.Paragraph>
                <strong>{t('confirmations:archiveActivity.danger')}</strong>
              </Text.Paragraph>
              <Text.Paragraph>
                {t('confirmations:archiveActivity.description')}
              </Text.Paragraph>
            </Stack>
          ),
        };
      }

      const milestoneName = milestone.getName();
      const recipientName = recipient.getFullName();

      return {
        id: activity._id,
        form: dialog.name,

        title: (
          <>
            <Text.Span importance="high">
              {t('permissions:patientMilestone.archiveActivity')}
            </Text.Span>
            {' — '}
            <Text.Span>{milestoneName}</Text.Span>
            {' — '}
            <Text.Span>{recipientName}</Text.Span>
          </>
        ),

        confirmText: t('remove'),
        expectedValue: milestoneName,
        expectedValueLabel: t('confirmations:archiveActivity.label'),

        message: (
          <Stack>
            <Text.Paragraph>
              {t('confirmations:archiveActivity.title')}
            </Text.Paragraph>
            <Text.Paragraph>
              <strong>{t('confirmations:archiveActivity.danger')}</strong>
            </Text.Paragraph>
            <Text.Paragraph>
              {t('confirmations:archiveActivity.description')}
            </Text.Paragraph>
          </Stack>
        ),
      };
    },
  ),
  withHandlers({
    // 🔍 Log cancel
    onCancel:
      ({ dispatch }) =>
      () => {
        console.log('[ArchiveActivityDialog] cancel');
        dispatch(dialog.close());
        dispatch(reset(dialog.name));
      },

    // 🔍 Log confirm + API lifecycle
    onConfirm:
      ({ dispatch, t }) =>
      (activityId) => {
        console.log('[ArchiveActivityDialog] confirm clicked', {
          activityId,
        });

        return dispatch(callMethod(apiArchiveActivity, { activityId }))
          .then(() => {
            console.log('[ArchiveActivityDialog] archive success');
            dispatch(reset(dialog.name));
            dispatch(dialog.close());
          })
          .then(notifySuccess(t('confirmations:archiveActivity.success')))
          .catch((err) => {
            console.error('[ArchiveActivityDialog] archive failed', err);
            notifyError()(err);
          });
      },
  }),
)(ConfirmRemoveObject);
